import { useState } from "react";
import { Search, Plus, Pencil, Trash2, Phone, Mail, MessageCircle, CalendarClock, StickyNote, Send } from "lucide-react";
import { Card, Badge, Dot, Drawer, Field, inputCls } from "./ui";
import { LeadForm } from "./LeadForm";
import { ESTADOS, INTERESES, INTERES_META, TIPOS_CONTACTO, fmtDate, relDate, fmtMoney, day } from "./store";

const ESTADO_CHIP = {
  "Nuevo": "bg-blue-50 text-blue-600 border-blue-200",
  "Contactado": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Reunión": "bg-amber-50 text-amber-700 border-amber-200",
  "Propuesta": "bg-purple-50 text-purple-600 border-purple-200",
  "Reserva": "bg-orange-50 text-orange-600 border-orange-200",
  "Contrato": "bg-violet-50 text-violet-600 border-violet-200",
  "Cerrado": "bg-emerald-50 text-emerald-600 border-emerald-200",
};

export const Leads = ({ data, upsertLead, removeLead, openNewLead, addLeadComment, user }) => {
  const { leads } = data;
  const [q, setQ] = useState("");
  const [fInteres, setFInteres] = useState("Todos");
  const [fEstado, setFEstado] = useState("Todos");
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [newInt, setNewInt] = useState({ fecha: day(0), tipo: "Llamada", resumen: "" });
  const [newComment, setNewComment] = useState("");

  const filtered = leads.filter((l) => {
    const t = q.toLowerCase();
    const okQ = !t || [l.nombre, l.email, l.telefono].some((v) => (v || "").toLowerCase().includes(t));
    return okQ && (fInteres === "Todos" || l.interes === fInteres) && (fEstado === "Todos" || l.estado === fEstado);
  });

  const selected = leads.find((l) => l.id === selectedId);

  const del = (id) => {
    if (window.confirm("¿Eliminar este lead de forma permanente? Se eliminará para todo el equipo.")) {
      removeLead(id);
      setSelectedId(null);
    }
  };

  const addInteraccion = () => {
    if (!newInt.resumen.trim()) return;
    upsertLead({ ...selected, interacciones: [...selected.interacciones, { ...newInt }] });
    setNewInt({ fecha: day(0), tipo: "Llamada", resumen: "" });
  };

  const submitComment = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    addLeadComment(selected.id, newComment.trim());
    setNewComment("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="leads-search"
            className={`${inputCls} pl-9`}
            placeholder="Buscar por nombre, email o teléfono..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select data-testid="leads-filter-interes" className={`${inputCls} w-auto`} value={fInteres} onChange={(e) => setFInteres(e.target.value)}>
          <option>Todos</option>
          {INTERESES.map((i) => <option key={i}>{i}</option>)}
        </select>
        <select data-testid="leads-filter-estado" className={`${inputCls} w-auto`} value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
          <option>Todos</option>
          {ESTADOS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button data-testid="leads-new-btn" onClick={openNewLead} className="inline-flex items-center gap-2 rounded-lg bg-[#2d5a3d] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3a7a50]">
          <Plus className="h-4 w-4" /> Nuevo lead
        </button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wide text-gray-400">
                {["Nombre", "Contacto", "Origen", "Perfil", "Interés", "Unid.", "Estado", "Próxima acción"].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSelectedId(l.id)} className="cursor-pointer border-b border-gray-50 transition-colors hover:bg-[#f5f0e8]/50">
                  <td className="px-4 py-3.5 font-medium text-gray-900">{l.nombre}</td>
                  <td className="px-4 py-3.5 text-xs text-gray-500">{l.email}<br />{l.telefono}</td>
                  <td className="px-4 py-3.5 text-xs text-gray-600">{l.origen}</td>
                  <td className="px-4 py-3.5 text-xs text-gray-600">{l.perfil}</td>
                  <td className="px-4 py-3.5"><Badge className={INTERES_META[l.interes]?.chip || "border-gray-200 bg-gray-50 text-gray-600"}><Dot color={INTERES_META[l.interes]?.dot || "#999"} />{l.interes}</Badge></td>
                  <td className="px-4 py-3.5 text-xs text-gray-600">{l.unidades} <span className="text-gray-400">({fmtMoney(l.unidades * 59600)})</span></td>
                  <td className="px-4 py-3.5"><Badge className={ESTADO_CHIP[l.estado] || "border-gray-200 bg-gray-50 text-gray-600"}>{l.estado}</Badge></td>
                  <td className="max-w-[180px] truncate px-4 py-3.5 text-xs text-gray-500" title={l.proximaAccion}>
                    {l.proximaAccion || "—"}
                    {l.fechaProxima && <span className={`block ${l.fechaProxima < day(0) ? "text-red-500" : "text-gray-400"}`}>{fmtDate(l.fechaProxima)} {relDate(l.fechaProxima) && `· ${relDate(l.fechaProxima)}`}</span>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-400">No hay leads que coincidan con los filtros</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="px-4 py-3 text-xs text-gray-400">{filtered.length} leads</p>
      </Card>

      {selected && (
        <Drawer title={selected.nombre} onClose={() => setSelectedId(null)}>
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge className={INTERES_META[selected.interes]?.chip || ""}><Dot color={INTERES_META[selected.interes]?.dot || "#999"} />{selected.interes}</Badge>
              <Badge className={ESTADO_CHIP[selected.estado] || ""}>{selected.estado}</Badge>
              <Badge className="border-gray-200 bg-gray-50 text-gray-600">{selected.perfil}</Badge>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              {[["Email", selected.email], ["Teléfono", selected.telefono], ["WhatsApp", selected.whatsapp], ["Origen", selected.origen],
                ["Unidades de interés", `${selected.unidades} (${fmtMoney(selected.unidades * 59600)})`], ["Presupuesto", selected.presupuesto],
                ["Próxima acción", selected.proximaAccion], ["Fecha próxima acción", fmtDate(selected.fechaProxima)]].map(([k, v]) => (
                <div key={k} className="rounded-xl bg-gray-50 px-4 py-3">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">{k}</p>
                  <p className="mt-0.5 text-gray-800">{v || "—"}</p>
                </div>
              ))}
            </div>

            {selected.notas && (
              <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 text-sm text-gray-700">
                <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600"><StickyNote className="h-3 w-3" /> Notas</p>
                {selected.notas}
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold text-gray-900">Historial de interacciones</h4>
              <div className="mt-3 space-y-3">
                {[...selected.interacciones].sort((a, b) => b.fecha.localeCompare(a.fecha)).map((i, idx) => (
                  <div key={idx} data-testid={`interaccion-${idx}`} className="flex gap-3 text-sm">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2d5a3d]/10 text-[#2d5a3d]">
                      {i.tipo === "Llamada" ? <Phone className="h-3.5 w-3.5" /> : i.tipo === "Email" ? <Mail className="h-3.5 w-3.5" /> : i.tipo === "WhatsApp" ? <MessageCircle className="h-3.5 w-3.5" /> : <CalendarClock className="h-3.5 w-3.5" />}
                    </span>
                    <div>
                      <p className="text-xs text-gray-400">{fmtDate(i.fecha)} · {i.tipo}</p>
                      <p className="text-gray-700">{i.resumen}</p>
                    </div>
                  </div>
                ))}
                {selected.interacciones.length === 0 && <p className="text-xs text-gray-400">Sin interacciones todavía</p>}
              </div>

              <div className="mt-4 space-y-3 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Añadir interacción</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Fecha"><input data-testid="int-fecha" type="date" className={inputCls} value={newInt.fecha} onChange={(e) => setNewInt({ ...newInt, fecha: e.target.value })} /></Field>
                  <Field label="Tipo">
                    <select data-testid="int-tipo" className={inputCls} value={newInt.tipo} onChange={(e) => setNewInt({ ...newInt, tipo: e.target.value })}>
                      {TIPOS_CONTACTO.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Resumen">
                  <textarea data-testid="int-resumen" rows={2} className={inputCls} value={newInt.resumen} onChange={(e) => setNewInt({ ...newInt, resumen: e.target.value })} placeholder="Resumen de la conversación..." />
                </Field>
                <button data-testid="int-add-btn" onClick={addInteraccion} className="rounded-lg bg-[#2d5a3d] px-4 py-2 text-xs font-semibold text-white hover:bg-[#3a7a50]">Añadir interacción</button>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900">Chat interno del equipo</h4>
              <div className="mt-3 space-y-3">
                {(!selected.comentarios || selected.comentarios.length === 0) ? (
                  <p className="text-xs text-gray-400">No hay notas internas todavía. ¡Sé el primero en escribir!</p>
                ) : (
                  [...selected.comentarios].sort((a, b) => b.fecha.localeCompare(a.fecha)).map((c) => (
                    <div key={c.id} data-testid={`comentario-${c.id}`} className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-[#2d5a3d]">{c.autor}</p>
                        <p className="text-[10px] text-gray-400">{fmtDate(c.fecha)}</p>
                      </div>
                      <p className="mt-1 text-sm text-gray-700">{c.texto}</p>
                    </div>
                  ))
                )}
              </div>
              <form onSubmit={submitComment} className="mt-3 flex gap-2">
                <input
                  data-testid="lead-comment-input"
                  className={inputCls}
                  placeholder="Escribe una nota rápida para el equipo..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <button data-testid="lead-comment-submit" type="submit" className="inline-flex shrink-0 items-center justify-center rounded-lg bg-[#2d5a3d] px-3 text-white hover:bg-[#3a7a50]">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>

            <div className="flex gap-3 border-t border-gray-100 pt-4">
              <button data-testid="lead-edit-btn" onClick={() => setEditing(selected)} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Pencil className="h-4 w-4" /> Editar lead
              </button>
              <button data-testid="lead-delete-btn" onClick={() => del(selected.id)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4" /> Eliminar lead
              </button>
            </div>
          </div>
        </Drawer>
      )}

      {editing && (
        <LeadForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={(lead) => { upsertLead(lead); setEditing(null); }}
        />
      )}
    </div>
  );
};
